import React from "react";
import {
  Autocomplete,
  AutocompleteCloseReason,
  Checkbox,
  CircularProgress,
  TextField,
  Typography,
  Box,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import dayjs from "dayjs";

export interface PastCampaign {
  id: string;
  name: string;
  scheduledTime: string;
  recipientCount?: number;
}

interface ExclusionCampaignSelectorProps {
  pastCampaigns: PastCampaign[];
  selectedExclusions: PastCampaign[];
  loading: boolean;
  openDropdown: boolean;
  onOpen: () => void;
  onClose: (event: React.SyntheticEvent, reason: AutocompleteCloseReason) => void;
  onChange: (newValue: PastCampaign[]) => void;
}

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const styles = {
  groupHeader: {
    fontWeight: 700,
    fontSize: "0.75rem",
    letterSpacing: "0.08em",
    backgroundColor: "#f5f5f5",
    padding: "6px 12px",
    color: "#555",
  },
  listContainer: {
    margin: 0,
    padding: 0,
  },
  listItem: {
    padding: "4px 8px",
    alignItems: "flex-start",
  },
  checkbox: {
    marginRight: 8,
    marginTop: 2,
  },
} as const;

const ExclusionCampaignSelector: React.FC<ExclusionCampaignSelectorProps> = ({
  pastCampaigns,
  selectedExclusions,
  loading,
  openDropdown,
  onOpen,
  onClose,
  onChange,
}) => {
  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      options={pastCampaigns}
      value={selectedExclusions}
      open={openDropdown}
      loading={loading}
      onOpen={onOpen}
      onClose={onClose}
      onChange={(_, newValue) => onChange(newValue)}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionLabel={(option) => option.name}
      groupBy={() => "PREVIOUS CAMPAIGNS"}
      renderGroup={(params) => (
        <li key={params.key}>
          <div style={styles.groupHeader}>{params.group}</div>
          <ul style={styles.listContainer}>{params.children}</ul>
        </li>
      )}
      renderOption={(props, option, { selected }) => {
        const { key, ...rest } = props;
        return (
          <li key={key} {...rest} style={styles.listItem}>
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              checked={selected}
              style={styles.checkbox}
            />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {option.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {option.scheduledTime
                  ? dayjs(option.scheduledTime).format("MMM D, YYYY")
                  : ""}
                {option.recipientCount != null
                  ? ` · ${option.recipientCount.toLocaleString()} recipients`
                  : ""}
              </Typography>
            </Box>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Exclude recipients from past campaigns"
          placeholder="Exclude recipients from past campaigns..."
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              endAdornment: (
                <>
                  {loading && !openDropdown ? <CircularProgress size={20} /> : null}
                  {params.slotProps.input.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      sx={{ mt: 3 }}
    />
  );
};

export default ExclusionCampaignSelector;
